/**
 * Fog of war.
 *
 * One byte per tile per civilisation, holding UNKNOWN, EXPLORED or VISIBLE.
 *
 * Visibility is rebuilt wholesale rather than tracked incrementally: demote
 * everything currently visible to explored, then light up what each unit can see.
 * On the performance budget — 800 units, sight radius 2, nineteen tiles each — that
 * is about fifteen thousand array writes, which is nothing. An incremental version
 * would be faster and would also have to get right what happens when a unit dies on
 * a tile two other units can partly see, which is a bug worth not having yet. It is
 * one function, so Phase 15 can make it incremental if the profiler ever asks.
 *
 * Alongside the level, each civilisation keeps a MEMORY of what it last saw on every
 * tile it has looked at (see CivMemory). The level says how much you know; the memory
 * says what you knew. Renderers must draw remembered tiles from the memory and never
 * from the live map, or a city founded inside your fog would appear out of nothing.
 * That is why memory is written here, in the same pass that lowers a tile to explored:
 * the last thing you see before the fog closes is exactly what you should remember.
 */

import type { Content } from '../../../content/schema/index.js';
import { hexSpiral, offsetToAxial } from '../hex/index.js';
import { hexIndex, tileCount } from '../state/grid.js';
import {
  VIS_EXPLORED,
  VIS_UNKNOWN,
  VIS_VISIBLE,
  type CivMemory,
  type GameState,
  type Id,
  type TileGrid,
} from '../state/types.js';
import { unitDef, unitsOf } from '../units/index.js';

export function createVisionPlane(map: TileGrid): Uint8Array {
  return new Uint8Array(tileCount(map));
}

export function createMemory(map: TileGrid): CivMemory {
  const n = tileCount(map);
  return {
    terrain: new Uint8Array(n),
    feature: new Uint8Array(n),
    improvement: new Uint8Array(n),
    road: new Uint8Array(n),
    cities: {},
  };
}

/** How far a unit standing where it is can see. High ground sees further. */
export function sightRadius(content: Content, map: TileGrid, index: number, sight: number): number {
  const terrain = content.terrain.get(map.terrain[index]!);
  const high = content.rules.vision.highGroundTerrain.includes(terrain.key);
  return sight + (high ? content.rules.vision.elevationBonus : 0);
}

/**
 * Rebuild one civilisation's view of the world.
 *
 * Returns the tiles that went from unknown to visible, ascending — the discoveries
 * worth reporting. Ascending rather than in the order they happened to be touched,
 * because the event ends up in the state hash.
 */
export function recomputeVision(state: GameState, content: Content, civId: Id): number[] {
  const plane = state.vision[civId];
  if (!plane) return [];

  const map = state.map;
  const memory = state.memory[civId];
  const wasUnknown: number[] = [];

  for (let i = 0; i < plane.length; i++) {
    if (plane[i] === VIS_VISIBLE) plane[i] = VIS_EXPLORED;
  }

  const lit: number[] = [];
  const light = (index: number): void => {
    if (plane[index] === VIS_UNKNOWN) wasUnknown.push(index);
    if (plane[index] !== VIS_VISIBLE) lit.push(index);
    plane[index] = VIS_VISIBLE;
  };

  for (const unit of unitsOf(state, civId)) {
    const centre = offsetToAxial(unit.col, unit.row);
    const here = hexIndex(map, centre);
    if (here === -1) continue;

    const def = unitDef(content, unit);
    const radius = sightRadius(content, map, here, def.sight);

    for (const h of hexSpiral(centre, radius)) {
      const index = hexIndex(map, h);
      if (index !== -1) light(index);
    }
  }

  // A city watches its own surroundings, so a civilisation does not go blind in its
  // own territory the moment its garrison marches off.
  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.owner !== civId) continue;
    const centre = offsetToAxial(city.col, city.row);
    const here = hexIndex(map, centre);
    if (here === -1) continue;
    for (const h of hexSpiral(centre, content.rules.vision.cityRadius)) {
      const index = hexIndex(map, h);
      if (index !== -1) light(index);
    }
  }

  // Every tile lit this pass is every tile now visible (all were dimmed first), so only
  // those need remembering — not the whole map (Phase 15).
  if (memory) rememberVisible(state, memory, lit.sort((a, b) => a - b));

  wasUnknown.sort((a, b) => a - b);
  return wasUnknown;
}

/**
 * Copy what is currently visible into the civilisation's memory.
 *
 * Every visible tile, not merely the newly lit ones: a tile you are still looking at
 * must keep pace with reality, or a city razed in front of you would linger.
 */
function rememberVisible(state: GameState, memory: CivMemory, visible: readonly number[]): void {
  const map = state.map;

  const cityAt = new Map<number, { owner: Id; name: string; population: number }>();
  for (const city of Object.values(state.cities)) {
    const index = city.row * map.width + city.col;
    cityAt.set(index, { owner: city.owner, name: city.name, population: city.population });
  }

  for (const i of visible) {
    memory.terrain[i] = map.terrain[i]!;
    memory.feature[i] = map.feature[i]!;
    memory.improvement[i] = map.improvement[i]!;
    memory.road[i] = map.road[i]!;

    const city = cityAt.get(i);
    if (city) memory.cities[i] = city;
    else delete memory.cities[i];
  }
}

/** What a civilisation believes is on a tile: the live truth if visible, else memory. */
export function remembered(
  state: GameState,
  civId: Id,
  index: number,
): { terrain: number; feature: number; improvement: number; city: { owner: Id; name: string; population: number } | null } | null {
  const level = state.vision[civId]?.[index] ?? VIS_UNKNOWN;
  if (level === VIS_UNKNOWN) return null;

  const memory = state.memory[civId];
  if (!memory) return null;
  return {
    terrain: memory.terrain[index]!,
    feature: memory.feature[index]!,
    improvement: memory.improvement[index]!,
    city: memory.cities[index] ?? null,
  };
}

/** Rebuild every civilisation's view. Returns discoveries keyed by civ id. */
export function recomputeAllVision(state: GameState, content: Content): Record<Id, number[]> {
  const out: Record<Id, number[]> = {};
  for (const civId of Object.keys(state.vision).map(Number).sort((a, b) => a - b)) {
    out[civId] = recomputeVision(state, content, civId);
  }
  return out;
}

/** How much of the map a civilisation has seen at least once. */
export function exploredFraction(state: GameState, civId: Id): number {
  const plane = state.vision[civId];
  if (!plane || plane.length === 0) return 0;
  let seen = 0;
  for (let i = 0; i < plane.length; i++) if (plane[i] !== VIS_UNKNOWN) seen++;
  return seen / plane.length;
}
